/**
 * @owner @Appboy/messaging-experience
 * @fileoverview this exists to ensure all values of a variable are exhausted in code
 * this will throw an compile time error if all values are not exhaustive
 * See https://dev.to/babak/exhaustive-type-checking-with-typescript-4l3f
 */

import * as Sentry from "@sentry/browser";

/**
 * Logs to sentry and console when an unexpected code path is reached. Use
 * inside the default branch of an exhaustive switch statement. Should fail at
 * compile time if you are missing a branch, though this doesn't always work
 * perfectly in non-strict code.
 *
 * Note that failure is effectively soft and this does not throw, so you still
 * need to return a fallback value from the default branch.
 */
export const exhaustiveCheck = (param: never) => {
  const errorMessage = `Exhaustive check failed; encountered ${JSON.stringify(param)}`;

  // Need to use captureException to ensure we get a stack trace.
  Sentry.captureException(new Error(errorMessage), {
    extra: {
      actual_value: param,
    },
  });
  console.error(errorMessage);
};

/**
 * Raises an exception when an unexpected code path is reached.
 */
export const strictExhaustiveCheck = (param: never): never => {
  exhaustiveCheck(param);
  throw new Error(`Strict exhaustive check failed, actual value: ${JSON.stringify(param)}`);
};
